import { useState } from 'react';
import type { KeyboardEvent } from 'react';
import Close from '@mui/icons-material/Close';
import Chip from '@mui/material/Chip';
import { Button, Input } from 'components/ui';
import {
  DashboardChipList,
  DashboardControlRow,
  DashboardControlStack,
} from './dashboardControls.styles';

interface DashboardStringListFieldProps {
  disabled?: boolean;
  maxItems?: number;
  onChange: (next: string[]) => void;
  placeholder?: string;
  testId?: string;
  value: readonly string[];
}

// Free-text entries (display tags, styling colors). Nothing in components/ui does token entry, and
// the two consumers are small enough that a chip list plus an Add button is the whole requirement.
const DashboardStringListField = ({
  disabled = false,
  maxItems,
  onChange,
  placeholder,
  testId,
  value,
}: DashboardStringListFieldProps) => {
  const [draft, setDraft] = useState('');
  const isFull = maxItems !== undefined && value.length >= maxItems;
  const trimmed = draft.trim();
  // Duplicates are rejected rather than deduped on save: two identical tags read as a mistake, and
  // silently dropping one would look like the Add button had failed.
  const canAdd = Boolean(trimmed) && !isFull && !value.includes(trimmed);

  const commit = () => {
    if (!canAdd) {
      return;
    }
    onChange([...value, trimmed]);
    setDraft('');
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      commit();
    }
  };

  return (
    <DashboardControlStack>
      {value.length > 0 && (
        <DashboardChipList>
          {value.map(entry => (
            <Chip
              key={entry}
              deleteIcon={<Close fontSize="small" />}
              disabled={disabled}
              label={entry}
              onDelete={() => onChange(value.filter(current => current !== entry))}
              size="small"
            />
          ))}
        </DashboardChipList>
      )}
      <DashboardControlRow>
        <Input
          data-testid={testId}
          disabled={disabled || isFull}
          onChange={event => setDraft(event.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={isFull ? `Limit of ${maxItems} reached` : placeholder}
          size="small"
          value={draft}
          visualVariant="compactOutlined"
        />
        <Button disabled={disabled || !canAdd} onClick={commit} sizeVariant="compact">
          Add
        </Button>
      </DashboardControlRow>
    </DashboardControlStack>
  );
};

export default DashboardStringListField;
